import { ReactElement } from 'react';

import WYSIWYG from "@components/WYSIWYG";
import { useMobile } from "@lib/hooks/useMobile";
import {GridContainer, GridWrapper, imageLoaderResponsive} from '@theme/lib';
import Image from 'next/image';

import { TextImageParagraph, WYSIWYGFragment } from '@/__generated__/graphql';
import { getLanguage } from '@/lib/config';
import StyledButton from '../Button';
import {StyledImage, StyledText, StyledTextAndImage, TextImageGridContainerEdge} from './TextImage.styles';
import {useTheme} from "styled-components";
import {ImagesGridContainerEdge} from "@components/Images";
import { bicentenaryBackgroundClass } from '@components/BicentenaryPatterns/util';

interface TextProps {
  text: {
    processed: string;
    format: string;
  }
  title?: string | null;
  cta?: {
    title: string;
    url: string;
  } | null;
  layout: string;
}

interface ImageProps {
  img: {
    altText: string | null;
    url: string;
    sm: string;
    md: string;
    lg: string;
    xl: string;
  }
  caption?: string | null;
  layout: string;
}

function TextComponent(data: TextProps): ReactElement<any> {
  const language = getLanguage();
  return (
    <StyledText className="text-image__description" lang={language !== 'en' ? language : undefined} layout={data.layout}>
      {data.title && (<h2>{data.title}</h2>)}
      <WYSIWYG
        id={'text-image__content'}
        text={data.text as WYSIWYGFragment['text']}
        alignment={'left'}
        textAlign={'left'}
      />
      {data.cta && (
        <StyledButton href={data.cta.url} primary lang={language !== 'en' ? language : undefined}>
          {data.cta.title}
        </StyledButton>)}
    </StyledText>
  );
};

function ImageComponent(data: ImageProps): ReactElement<any> {
  return (
    <StyledImage layout={data.layout}>
      <Image
        src={data.img.url}
        alt={data.img.altText || ''}
        style={{ width: '100%', height: 'auto', objectFit: 'cover' }}
        width="592"
        height="380"
        loader={imageLoaderResponsive(592, 380)}
      />
      {data.caption &&
        <footer>
          <small className="caption">
            {data.caption}
          </small>
        </footer>}
    </StyledImage>
  );
}

export default function TextImage(data: TextImageParagraph): ReactElement<any> {
  const isMobile = useMobile();
  const theme = useTheme();

  const innerContent = (
    <GridWrapper alignment={'center-wide'}>
      <StyledTextAndImage layout={data.textImageLayout}>
        {(isMobile || data.textImageLayout === 'image_text') ?
          <>
            <ImageComponent
              img={data.image}
              layout={data.textImageLayout}
              caption={data.caption}
            />
            <TextComponent
              title={data.title}
              text={data.text}
              layout={data.textImageLayout}
              cta={data.callToAction}
            />
          </>
          :
          <>
            <TextComponent
              title={data.title}
              text={data.text}
              layout={data.textImageLayout}
              cta={data.callToAction}
            />
            <ImageComponent
              img={data.image}
              layout={data.textImageLayout}
              caption={data.caption}
            />
          </>
        }
      </StyledTextAndImage>
    </GridWrapper>
  );

  if (theme.key === 'bicentenary') {
    return (
      <TextImageGridContainerEdge className={`${data.__typename} ${bicentenaryBackgroundClass(data)}`}>
        <GridContainer>
          {innerContent}
        </GridContainer>
      </TextImageGridContainerEdge>
    )
  }

  return (
    <GridContainer className={data.__typename}>
      {innerContent}
    </GridContainer>
  );

}
